import type { ConditionalRule, FormField, FormSection } from '@/types/config-governance';

/**
 * The renderer's contract for conditional visibility, as pure functions.
 *
 * Core's validator promises conditions resolve TOP-DOWN in a single pass over
 * the fields array — a condition may only reference a field defined EARLIER,
 * never itself, never forward — and refuses schemas that break it. This module
 * is the other half of that promise: the preview pane resolves visibility in
 * exactly the single pass the validator guarantees is enough, and the
 * conditional-on picker offers exactly the fields the validator would accept.
 * If the two halves disagreed, the preview would show a form the server
 * refuses, or the picker would offer a reference the server rejects.
 */

export interface VisibilityResult {
  visibleSections: Set<string>;
  visibleFields: Set<string>;
}

type AnswerMap = Record<string, string | number | boolean | undefined>;

/**
 * Loose equality on purpose, in one narrow way: a SELECT's answer arrives as a
 * string even when the rule was authored as a number or boolean, because HTML
 * form controls speak strings. Comparing String(a) === String(b) mirrors what
 * a real renderer must do, and keeps `equals: true` matching the "true" a
 * checkbox-backed select produces. Everything else is exact.
 */
function matches(rule: ConditionalRule, answers: AnswerMap): boolean {
  const answer = answers[rule.fieldCode];
  if (answer === undefined) return false;
  return String(answer) === String(rule.equals);
}

/**
 * One top-down pass, exactly like the product-side renderer:
 *
 *  - A field is visible when its own condition matches (or it has none), AND
 *    its section is visible, AND the field the condition points at is itself
 *    visible — an answer inside a hidden section is treated as not given,
 *    because the applicant never saw the question. The validator's
 *    earlier-only rule is what makes the single pass sufficient: by the time
 *    a condition is evaluated, its target's visibility is already decided.
 *  - A section is visible when its condition matches against a VISIBLE
 *    earlier field (the validator requires the target to sit in an earlier
 *    section).
 *
 * Fields are evaluated in array order; sections in ordinal order. Sections
 * are interleaved by evaluating each section right before its fields would be
 * reached, which ordinal ordering gives us directly.
 */
export function evaluateVisibility(
  sections: FormSection[],
  fields: FormField[],
  answers: AnswerMap,
): VisibilityResult {
  const visibleSections = new Set<string>();
  const visibleFields = new Set<string>();

  const orderedSections = [...sections].sort((a, b) => a.ordinal - b.ordinal);
  for (const section of orderedSections) {
    if (!section.conditionalOn) {
      visibleSections.add(section.code);
    } else if (
      // The controlling field must be visible itself — a hidden question's
      // stale answer must not keep a section open.
      visibleFields.has(section.conditionalOn.fieldCode) &&
      matches(section.conditionalOn, answers)
    ) {
      visibleSections.add(section.code);
    }

    // Fields are walked per section so that by the time a LATER section's
    // condition is read, every field of the earlier sections has a decided
    // visibility. Within a section, array order is the authoring order the
    // validator checked forward references against.
    for (const field of fields) {
      if (field.sectionCode !== section.code) continue;
      if (!visibleSections.has(section.code)) continue;
      if (!field.conditionalOn) {
        visibleFields.add(field.code);
        continue;
      }
      if (visibleFields.has(field.conditionalOn.fieldCode) && matches(field.conditionalOn, answers)) {
        visibleFields.add(field.code);
      }
    }
  }

  return { visibleSections, visibleFields };
}

/**
 * The fields a condition on `subjectCode` may legally reference: strictly
 * EARLIER in the fields array, and never the subject itself — the exact rule
 * FormSchemaService.validate enforces, offered up front so the picker cannot
 * author a forward reference the server would refuse.
 *
 * For a NEW field (subjectCode not in the array yet) every existing field is
 * earlier, so all of them are offered.
 */
export function eligibleConditionTargets(fields: FormField[], subjectCode: string): FormField[] {
  const subjectIndex = fields.findIndex((field) => field.code === subjectCode);
  const limit = subjectIndex === -1 ? fields.length : subjectIndex;
  return fields.slice(0, limit).filter((field) => field.code !== subjectCode);
}

/**
 * The fields a SECTION's condition may reference: fields living in sections
 * with a strictly smaller ordinal — the validator's "an earlier section"
 * rule. A section being created gets the largest ordinal by construction, so
 * passing its intended ordinal covers both edit and create.
 */
export function eligibleSectionConditionTargets(
  sections: FormSection[],
  fields: FormField[],
  sectionOrdinal: number,
): FormField[] {
  const earlier = new Set(
    sections.filter((section) => section.ordinal < sectionOrdinal).map((section) => section.code),
  );
  return fields.filter((field) => earlier.has(field.sectionCode));
}
